/**
 * Reseller dashboard RPC definitions (CLA-536).
 *
 * Reseller-facing authenticated endpoint for the caller's downstream fleet: the
 * attributed customers of the reseller the caller belongs to, each with its
 * agents. The reseller is resolved on the backend from `CurrentActor` (the
 * caller's single reseller-role customer membership) — never from client input,
 * so one reseller can never read another's customers.
 *
 * NO secrets ever cross this wire: only derived host strings
 * (`gatewayHost`/`webHost`) are surfaced, never `gatewayToken`, `frpToken`,
 * `agentSecret` or `vncPassword`. Read-only — reaching the agents' services is a
 * separate ticket.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { BrowserKind, CloudProvider, DeploymentType } from "@fm/db/models/AgentInstallation";
import { StripeAccountStatus } from "@fm/db/models/ResellerProfile";
import { Schema } from "effect";
import { AgentSummary } from "../admin/agents.rpc.ts";
import { NodeHealth } from "../admin/nodes.rpc.ts";
import { BillSubscribeResult } from "../admin/resellers.rpc.ts";
import { AuthMiddleware, EnsureUserAuthMiddleware } from "../auth.tags.ts";
import {
  AgreementNotAccepted,
  AgreementPdfError,
  AgreementStorageError,
  AgreementStorageNotConfigured,
  DatabaseError,
  Forbidden,
  OnboardingLinkError,
  RecordNotFoundError,
  SubscriptionError,
  Unauthorized,
  ValidationError,
  ValkeyError,
} from "../errors.ts";

/**
 * One agent installation owned by a reseller-attributed customer. Derived
 * entirely on the backend from the reseller's attributed-customer set — never
 * from client input.
 *
 * `health` is the Valkey-heartbeat verdict the fleet uses; `lastSeenAt` the
 * heartbeat moment (null when never seen). `gatewayHost`/`webHost` are the
 * derived single-label customer hosts (null when the customer has no slug) —
 * NEVER any secret.
 *
 * @since 1.0.0
 */
export const ResellerAgentSummary = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  deploymentType: Schema.NullOr(DeploymentType),
  status: Schema.String,
  health: NodeHealth,
  lastSeenAt: Schema.NullOr(Schema.DateTimeUtc),
  region: Schema.String,
  agentVersion: Schema.NullOr(Schema.String),
  gatewayHost: Schema.NullOr(Schema.String),
  webHost: Schema.NullOr(Schema.String),
});
export type ResellerAgentSummary = Schema.Schema.Type<typeof ResellerAgentSummary>;

/**
 * One attributed customer of the resolved reseller, with its live agents
 * (excluding deleted/deprovisioned), ordered by agent name. `slug` is the
 * single-label host component (null when unset). Only identity + fleet fields
 * cross — never the customer's billing/Stripe identifiers.
 *
 * @since 1.0.0
 */
export const ResellerCustomerSummary = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  slug: Schema.NullOr(Schema.String),
  agents: Schema.Array(ResellerAgentSummary),
});
export type ResellerCustomerSummary = Schema.Schema.Type<typeof ResellerCustomerSummary>;

/**
 * Response for `resellers.customers.list`: the resolved reseller's own account
 * name (`resellerName`, from the reseller-role customer — server truth, so the
 * dashboard header renders the reseller's name rather than the signed-in
 * person's) plus its attributed customers (ordered by customer name), each with
 * its agents.
 *
 * @since 1.0.0
 */
export const ResellerCustomerListResponse = Schema.Struct({
  resellerName: Schema.String,
  // Per-reseller flag gating the (later) end-customer self-serve procurement
  // flow. Surfaced so the reseller dashboard can render its state (phase 2);
  // `false` when the reseller has no profile row yet.
  customerSelfServeEnabled: Schema.Boolean,
  customers: Schema.Array(ResellerCustomerSummary),
});
export type ResellerCustomerListResponse = Schema.Schema.Type<typeof ResellerCustomerListResponse>;

/**
 * Reseller-facing dashboard RPC group.
 *
 * `resellers.customers.list` resolves the reseller from `CurrentActor` (the
 * caller's single reseller-role membership) and returns that reseller's
 * attributed customers + their agents. A caller with no reseller membership is
 * {@link Forbidden}; a caller ambiguously in more than one reseller is also
 * {@link Forbidden} (surfaced + logged — no v1 switcher). No payload: the
 * target is never a client-supplied customer/reseller id.
 *
 * @since 1.0.0
 */
/**
 * A recorded acceptance summary — the caller's own executed sign, surfaced by
 * `resellers.agreement.status` (inside an `Option`) and returned by
 * `resellers.agreement.accept`. No PDF bytes or S3 key cross the wire.
 *
 * @since 1.0.0
 */
export const AgreementAcceptanceSummary = Schema.Struct({
  version: Schema.String,
  acceptedAt: Schema.DateTimeUtc,
  signerName: Schema.String,
  signerTitle: Schema.String,
});
export type AgreementAcceptanceSummary = Schema.Schema.Type<typeof AgreementAcceptanceSummary>;

/**
 * Response for `resellers.agreement.status`: the current agreement version + its
 * markdown source (for display), whether the caller has accepted THIS version,
 * and the caller's latest acceptance summary when present.
 *
 * @since 1.0.0
 */
export const AgreementStatusResponse = Schema.Struct({
  version: Schema.String,
  agreementMarkdown: Schema.String,
  accepted: Schema.Boolean,
  acceptance: Schema.Option(AgreementAcceptanceSummary),
});
export type AgreementStatusResponse = Schema.Schema.Type<typeof AgreementStatusResponse>;

/**
 * Response for `resellers.agreement.accept`: the recorded (or pre-existing,
 * idempotent) acceptance summary.
 *
 * @since 1.0.0
 */
export const AgreementAcceptanceResponse = AgreementAcceptanceSummary;
export type AgreementAcceptanceResponse = Schema.Schema.Type<typeof AgreementAcceptanceResponse>;

/**
 * Response for `resellers.agreement.downloadUrl`: a short-lived presigned URL to
 * the caller's own executed PDF.
 *
 * @since 1.0.0
 */
export const AgreementDownloadUrlResponse = Schema.Struct({ url: Schema.String });
export type AgreementDownloadUrlResponse = Schema.Schema.Type<typeof AgreementDownloadUrlResponse>;

/**
 * Response for `resellers.stripe.ensureAccount`: the caller's own connected
 * account id + its derived status, whether freshly created or already present
 * (the op is idempotent).
 *
 * @since 1.0.0
 */
export const ResellerStripeAccountResponse = Schema.Struct({
  stripeAccountId: Schema.String,
  status: StripeAccountStatus,
});
export type ResellerStripeAccountResponse = Schema.Schema.Type<typeof ResellerStripeAccountResponse>;

/**
 * Response for `resellers.stripe.onboardingLink`: a freshly-minted hosted
 * onboarding link plus its expiry (account links are short-lived + single-use).
 * `expiresAt` is a moment-in-time.
 *
 * @since 1.0.0
 */
export const ResellerStripeOnboardingLinkResponse = Schema.Struct({
  url: Schema.String,
  expiresAt: Schema.DateTimeUtc,
});
export type ResellerStripeOnboardingLinkResponse = Schema.Schema.Type<typeof ResellerStripeOnboardingLinkResponse>;

/**
 * Response for `resellers.stripe.status`: live onboarding state for the caller's
 * own connected account. `hasAccount` is `false` (with null id/status and empty
 * requirements) when no connected account exists yet — a first-class success
 * state the wizard renders on load, never an error. When `hasAccount` is `true`
 * the fields mirror the admin onboarding-status read (derived status, currently
 * due requirement keys, charges/payouts flags), sourced live from Stripe.
 *
 * @since 1.0.0
 */
export const ResellerStripeStatusResponse = Schema.Struct({
  hasAccount: Schema.Boolean,
  stripeAccountId: Schema.NullOr(Schema.String),
  status: Schema.NullOr(StripeAccountStatus),
  requirementsDue: Schema.Array(Schema.String),
  chargesEnabled: Schema.Boolean,
  payoutsEnabled: Schema.Boolean,
});
export type ResellerStripeStatusResponse = Schema.Schema.Type<typeof ResellerStripeStatusResponse>;

/**
 * Input for the reseller start-invite resolve / bind endpoints: the plaintext
 * invite token from the start link. The server hashes it and looks the reseller
 * profile up by `reseller_profiles.invite_token_hash`.
 *
 * @since 1.0.0
 */
export const ResellerInviteTokenInput = Schema.Struct({
  token: Schema.NonEmptyTrimmedString,
});
export type ResellerInviteTokenInput = Schema.Schema.Type<typeof ResellerInviteTokenInput>;

/**
 * The lifecycle state of a resolved start invite:
 *
 * - `valid` — unexpired and not yet accepted; the invitee should sign up + bind.
 * - `accepted` — already bound once but still unexpired, so a re-login resumes.
 * - `expired` — past its window; the landing page shows a dead-link message.
 *
 * An unknown token never reaches here — it fails with {@link OnboardingLinkError}
 * (`reason: "invalid"`).
 *
 * @since 1.0.0
 */
export const ResellerInviteStatus = Schema.Literal("valid", "accepted", "expired");
export type ResellerInviteStatus = Schema.Schema.Type<typeof ResellerInviteStatus>;

/**
 * Response for `resellers.onboarding.resolveInvite`: the invite lifecycle state
 * plus the reseller's display name for the landing-page greeting. Never returns
 * any id or secret beyond what the page needs.
 *
 * @since 1.0.0
 */
export const ResellerInviteView = Schema.Struct({
  status: ResellerInviteStatus,
  resellerName: Schema.String,
});
export type ResellerInviteView = Schema.Schema.Type<typeof ResellerInviteView>;

/**
 * Response for `resellers.onboarding.bindInviteSignup`: the reseller the caller
 * is now bound to (customer id + display name). `bound` is always `true` on
 * success — a failure to bind surfaces as a typed error instead.
 *
 * @since 1.0.0
 */
export const ResellerInviteBindResult = Schema.Struct({
  bound: Schema.Boolean,
  resellerCustomerId: Schema.String,
  resellerName: Schema.String,
});
export type ResellerInviteBindResult = Schema.Schema.Type<typeof ResellerInviteBindResult>;

/**
 * Payload for `resellers.customers.agents.procure`: add an agent to one of the
 * caller-reseller's OWN attributed customers and open the billed direct-charge
 * subscription for it on the reseller's connected account. The reseller is
 * resolved from `CurrentActor` (never client input); `sourceCustomerId` must be
 * one of that reseller's attributed customers (a {@link Forbidden} otherwise).
 *
 * `deploymentType`/`cloudProvider`/`region`/`name` shape the agent exactly as
 * the admin create does (`cloud-vps` requires a `region`). `monthlyAmountCents`
 * is an optional per-add price override (minor units) — omitted, the reseller's
 * standing `subscription_price_id` is used; provided, a matching recurring price
 * is find-or-created on the connected account. `signupAmountCents` controls the
 * one-time setup line on invoice #1 INDEPENDENTLY of the monthly override:
 * omitted, the reseller's standing `signup_price_id` rides invoice #1 when
 * present; `> 0` adds a one-time line at that amount (find-or-created on the
 * connected account, never persisted as a standing rate); `0` waives the signup
 * line entirely, even when a stored signup price exists.
 *
 * @since 1.0.0
 */
export const ResellerProcureAgentInput = Schema.Struct({
  sourceCustomerId: Schema.NonEmptyTrimmedString,
  deploymentType: DeploymentType,
  cloudProvider: Schema.optional(CloudProvider),
  region: Schema.optional(Schema.String),
  name: Schema.optional(Schema.NonEmptyTrimmedString),
  /** Which browser to provision (chrome | camoufox). Omitted = "chrome". */
  browser: Schema.optional(BrowserKind),
  monthlyAmountCents: Schema.optional(Schema.Int),
  signupAmountCents: Schema.optional(Schema.Int),
});
export type ResellerProcureAgentInput = Schema.Schema.Type<typeof ResellerProcureAgentInput>;

/**
 * Response for `resellers.customers.agents.procure`: the freshly-created agent
 * (platform seat billing skipped — the connected-account subscription bills it)
 * plus the opened subscription's id + status + the first invoice's
 * `hostedInvoiceUrl` the reseller's customer pays. Paying that invoice drives
 * the existing `invoice.paid` webhook, which writes the commission accrual AND
 * dispatches provisioning for the created agent — no new code.
 *
 * @since 1.0.0
 */
export const ResellerProcureAgentResult = Schema.Struct({
  agent: AgentSummary,
  subscription: BillSubscribeResult,
});
export type ResellerProcureAgentResult = Schema.Schema.Type<typeof ResellerProcureAgentResult>;

export class ResellerRpcs extends RpcGroup.make(
  // resolveInvite is UNAUTHENTICATED (no middleware): the invite token is the
  // credential, so the landing page resolves the reseller display name + invite
  // status for a signed-out recipient BEFORE Clerk signup — mirrors the customer
  // `onboarding/resolveLink`. An unknown token fails `OnboardingLinkError`.
  Rpc.make("resellers.onboarding.resolveInvite", {
    payload: ResellerInviteTokenInput,
    success: ResellerInviteView,
    error: Schema.Union(OnboardingLinkError, RecordNotFoundError, DatabaseError),
  }),
  // bindInviteSignup runs for a JUST-signed-up Clerk user who may have no person
  // row yet, so it uses {@link EnsureUserAuthMiddleware} (NOT the plain
  // {@link AuthMiddleware}, which would 401 a fresh sign-up). Binding is
  // token-based (email-independent): a signup on a DIFFERENT address than the
  // intake email still ends bound to the reseller.
  Rpc.make("resellers.onboarding.bindInviteSignup", {
    payload: ResellerInviteTokenInput,
    success: ResellerInviteBindResult,
    error: Schema.Union(Unauthorized, OnboardingLinkError, RecordNotFoundError, DatabaseError),
  }).middleware(EnsureUserAuthMiddleware),
  Rpc.make("resellers.customers.list", {
    success: ResellerCustomerListResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, ValkeyError),
  }).middleware(AuthMiddleware),
  /**
   * Procure an agent for one of the caller-reseller's OWN attributed customers:
   * create the agent (platform seat billing skipped), open the billed
   * direct-charge subscription on the reseller's connected account (stamped with
   * the new agent's `agentInstallationId` so agent-scoped rate exceptions +
   * accrual tracing apply), and return the hosted invoice the customer pays. The
   * reseller is resolved from `CurrentActor`; a non-attributed / direct customer
   * is {@link Forbidden}, and a not-yet-onboarded connected account is a
   * {@link SubscriptionError} raised BEFORE anything is created.
   */
  Rpc.make("resellers.customers.agents.procure", {
    payload: ResellerProcureAgentInput,
    success: ResellerProcureAgentResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      SubscriptionError,
      ValidationError,
    ),
  }).middleware(AuthMiddleware),
  /**
   * The current agreement (version + markdown) plus whether the caller's
   * reseller has accepted THIS version, resolved from `CurrentActor` — never a
   * client-supplied reseller id.
   */
  Rpc.make("resellers.agreement.status", {
    success: AgreementStatusResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AuthMiddleware),
  /**
   * Click-sign the current agreement with a typed signature (name + title). The
   * server bakes the executed PDF, stores it, records the acceptance, and points
   * `reseller_profiles.agreement_url` at the PDF. Idempotent per (customer,
   * version): a repeat call returns the existing acceptance.
   */
  Rpc.make("resellers.agreement.accept", {
    payload: {
      signerName: Schema.NonEmptyString,
      signerTitle: Schema.NonEmptyString,
    },
    success: AgreementAcceptanceResponse,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      DatabaseError,
      AgreementStorageNotConfigured,
      AgreementStorageError,
      AgreementPdfError,
    ),
  }).middleware(AuthMiddleware),
  /**
   * A short-lived presigned URL to the caller's own latest executed PDF.
   */
  Rpc.make("resellers.agreement.downloadUrl", {
    success: AgreementDownloadUrlResponse,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      DatabaseError,
      RecordNotFoundError,
      AgreementStorageNotConfigured,
      AgreementStorageError,
    ),
  }).middleware(AuthMiddleware),
  /**
   * Idempotently ensure the caller's OWN reseller connected account exists,
   * creating it on first call and returning the existing account/status
   * thereafter. Scoped to the caller's reseller via `CurrentActor` — never a
   * client-supplied id. Gated on current-version agreement acceptance
   * ({@link AgreementNotAccepted} otherwise).
   */
  Rpc.make("resellers.stripe.ensureAccount", {
    success: ResellerStripeAccountResponse,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      DatabaseError,
      RecordNotFoundError,
      SubscriptionError,
      AgreementNotAccepted,
    ),
  }).middleware(AuthMiddleware),
  /**
   * Mint a fresh Stripe-hosted onboarding Account Link for the caller's OWN
   * connected account. Return/refresh URLs are server-derived (the reseller
   * wizard Stripe step) — never client input. Gated on current-version
   * agreement acceptance ({@link AgreementNotAccepted} otherwise) and on the
   * connected account existing ({@link RecordNotFoundError} otherwise).
   */
  Rpc.make("resellers.stripe.onboardingLink", {
    success: ResellerStripeOnboardingLinkResponse,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      DatabaseError,
      RecordNotFoundError,
      SubscriptionError,
      AgreementNotAccepted,
    ),
  }).middleware(AuthMiddleware),
  /**
   * Live onboarding status for the caller's OWN connected account. Returns a
   * typed "no account yet" success state (`hasAccount: false`) rather than an
   * error when no connected account exists — the wizard calls this on load,
   * before `ensureAccount`. NOT agreement-gated.
   */
  Rpc.make("resellers.stripe.status", {
    success: ResellerStripeStatusResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, SubscriptionError),
  }).middleware(AuthMiddleware),
) {}
